import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SEO } from "../components/SEO";
import { LightboxModal } from "../components/LightboxModal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { fetchCmsCollection } from "../lib/cms";

export function GalleryPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("kategori") || searchParams.get("cat") || "alle";

  const [galleries, setGalleries] = useState([]);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    async function loadGalleries() {
      const data = await fetchCmsCollection("galleri");
      setGalleries(data);
    }
    loadGalleries();
  }, []);

  useEffect(() => {
    const cat = searchParams.get("kategori") || searchParams.get("cat");
    if (cat) {
      setActiveCategory(cat);
    }
  }, [searchParams]);

  const categories = [
    { id: "alle", label: "Alle" },
    { id: "flise-og-klinkearbejde", label: "Flise- og klinkearbejde" },
    { id: "badevaerelser", label: "Badeværelser" },
    { id: "reparationer", label: "Reparationer" },
    { id: "renovering", label: "Renovering" },
    { id: "tilbygning", label: "Tilbygning" },
    { id: "nybyggeri", label: "Nybyggeri" }
  ];

  const filteredGalleries = (activeCategory === "alle"
    ? galleries
    : galleries.filter(
        (g) =>
          g.service_slug === activeCategory ||
          g.slug === activeCategory ||
          g.service_slug?.includes(activeCategory)
      )
  ).filter((g) => g.id !== "ff24bfb4-6987-4b57-97bf-29526cd704bd" && g.title !== "Renovering");

  const openLightbox = (imagesList, index = 0) => {
    setSelectedImages(imagesList);
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col pt-28 pb-16 bg-[#f3f4f6]">
      <SEO
        title="Galleri - Kokholm Byg"
        description="Billedgalleri af gennemførte murer- og byggeprojekter af Kokholm Byg. Se flisearbejde, badeværelsesrenoveringer, facader og nybyggeri."
      />

      {/* Hero Header */}
      <section 
        style={{ marginTop: '-40px' }}
        className="bg-[#1e2329] text-white py-14 border-b border-[#2d343f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#c06228] text-xs font-bold uppercase tracking-wider bg-[#272d36] px-3 py-1 rounded border border-[#373f4b]">
              Billedgalleri & Referencer
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3 text-white">
              Galleri
            </h1>
            <p className="text-gray-300 text-sm sm:text-base mt-2">
              Udforsk et udvalg af vores gennemførte murer- og byggeprojekter i Holstebro og omegn.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter Navigation */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-30 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? "bg-[#c06228] text-white shadow-sm"
                    : "bg-[#f3f4f6] text-[#1e2329] hover:bg-gray-200 border border-gray-300"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Galleries Display Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Featured Before & After for Reparationer */}
          {(activeCategory === "alle" || activeCategory === "reparationer") && (
            <div className="space-y-4">
              <div className="border-b border-gray-200 pb-2">
                <span className="text-[#c06228] text-xs font-bold uppercase tracking-wider">
                  Før & Efter
                </span>
                <h2 className="text-xl sm:text-2xl font-bold text-[#1e2329]">
                  Reparationsarbejde
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8210.jpg"
                  afterImage="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8211.jpg"
                  beforeLabel="Før"
                  afterLabel="Efter"
                  title="Murværk & facadereparation"
                  onOpenLightbox={(idx = 0) => {
                    const repGallery = galleries.find((g) => g.service_slug === "reparationer" || g.slug === "reparationer");
                    const imgs = repGallery?.images || [
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8210.jpg",
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8211.jpg",
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/a-sterbrogade-fa-r.jpg",
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/a-sterborgade-efter.jpg"
                    ];
                    openLightbox(imgs, idx);
                  }}
                />

                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/a-sterbrogade-fa-r.jpg"
                  afterImage="https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/a-sterborgade-efter.jpg"
                  beforeLabel="Før"
                  afterLabel="Efter"
                  title="Reparation på Østerbrogade"
                  onOpenLightbox={(idx = 0) => {
                    const repGallery = galleries.find((g) => g.service_slug === "reparationer" || g.slug === "reparationer");
                    const imgs = repGallery?.images || [
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8210.jpg",
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/img-8211.jpg",
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/a-sterbrogade-fa-r.jpg",
                      "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/a-sterborgade-efter.jpg"
                    ];
                    openLightbox(imgs, idx + 2);
                  }}
                />
              </div>
            </div>
          )}

          {filteredGalleries.length === 0 ? (
            <div className="bg-white p-12 rounded border border-gray-200 text-center space-y-3">
              <i className="fa-solid fa-images text-4xl text-gray-300" aria-hidden="true" />
              <h3 className="text-lg font-bold text-[#1e2329]">Ingen projekter i denne kategori endnu</h3>
              <p className="text-gray-500 text-sm">Prøv at vælge "Alle" for at se alle vores billeder.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredGalleries.map((item) => {
                const cover = Array.isArray(item.cover_image) && item.cover_image[0]
                  ? item.cover_image[0]
                  : (Array.isArray(item.images) && item.images[0] ? item.images[0] : "https://assets.auxodev.dk/assets/2f17f913-822d-430f-9ea1-0cc9e6665c42/breaker.jpg");

                const imagesList = Array.isArray(item.images) && item.images.length > 0 ? item.images : [cover];

                return (
                  <div
                    key={item.id || item.slug}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-200 flex flex-col justify-between"
                  >
                    <div>
                      {/* Cover Photo */}
                      <div
                        onClick={() => openLightbox(imagesList, 0)}
                        className="relative aspect-4/3 overflow-hidden bg-gray-100 cursor-pointer group"
                      >
                        <img
                          src={cover}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="bg-white/90 text-[#1e2329] text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-2">
                            <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                            <span>Vis {imagesList.length} billeder</span>
                          </span>
                        </div>
                      </div>

                      {/* Content Info */}
                      <div className="p-6 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-bold text-lg text-[#1e2329]">
                            {item.title}
                          </h3>
                          <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded shrink-0">
                            {imagesList.length} foto{imagesList.length > 1 ? "s" : ""}
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-gray-600 text-xs sm:text-sm leading-relaxed line-clamp-3">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Footer Trigger */}
                    <div className="px-6 pb-6 pt-2 border-t border-gray-50 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => openLightbox(imagesList, 0)}
                        className="text-xs font-semibold text-[#c06228] hover:text-[#aa521d] flex items-center gap-1.5 transition-colors"
                      >
                        <i className="fa-solid fa-images" aria-hidden="true" />
                        <span>Åbn fuld skærm</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        images={selectedImages}
        currentIndex={currentImageIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : selectedImages.length - 1))}
        onNext={() => setCurrentImageIndex((prev) => (prev < selectedImages.length - 1 ? prev + 1 : 0))}
      />
    </div>
  );
}
